import { useEffect, useRef } from 'react'
import { TerraDraw, TerraDrawLineStringMode, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw'
import type { GeoJSONStoreFeatures, TerraDrawEventListeners } from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'
import type { LineString, Polygon, Position } from 'geojson'
import { useMap } from './mapContext'
import { GEOJSON_LAYER_PREFIX } from './constants'

interface GeoPoint {
  lat: number
  lon: number
}

export interface VertexEditSessionProps {
  /** Single-ring polygon or open line, in geo coords. Asserted single-ring —
   *  see `assertValidShape` below for exactly what that means for a prop
   *  shape that structurally cannot carry more than one ring. */
  shape: { kind: 'polygon' | 'line'; points: GeoPoint[] }
  /** Visual accents for the lifted feature (stroke/fill/handles). Captured
   *  live: a change after mount re-styles the terra-draw feature in place.
   *  `fill: null` draws the polygon with NO fill — distinct from an omitted
   *  fill, which leaves terra-draw's own default wash in place. */
  style: { stroke: string; fill?: string | null }
  /** Continuous vertex updates, geo coords, index-addressed — the exact
   *  events the editor's history model consumes. */
  onVertexMove: (index: number, lat: number, lon: number) => void
  onVertexInsert: (afterIndex: number, lat: number, lon: number) => void
  onVertexDelete: (index: number) => void
  /** Fired once when a drag gesture starts (history snapshot hook). See the
   *  module doc comment on `gestureActiveRef` for how this is derived — the
   *  library has no dedicated "drag started" event at the `TerraDraw` level. */
  onDragStart?: () => void
}

// The ONE feature this component itself ever adds carries this fixed id —
// set explicitly on its own `addFeatures` call below, which is why `getId`
// (below) is never asked to mint an id FOR it: `GeoJSONStore.load` (read
// directly out of the installed terra-draw.modern.js) only calls
// `idStrategy.getId()` for a feature whose `id` is still null/undefined.
// Kept as a plain constant (not minted per mount) so tests have a known id to
// assert `addFeatures`/`updateFeatureGeometry` calls against, and so
// `handleChange`'s `ids.includes(FEATURE_ID)` guard has a fixed target.
const FEATURE_ID = 'vertex-edit-session-feature'

// `idStrategy` is NOT only asked for OUR feature's id — TerraDrawSelectMode's
// own interactive machinery (midpoint markers, coordinate/selection-point
// handles — `coordinates: { midpoints: true, ... }` below) creates its OWN
// helper features with no id of their own, and `GeoJSONStore.load` mints one
// for each of THOSE via this same strategy. A version that unconditionally
// returned the fixed `FEATURE_ID` (this file's very first cut) therefore
// handed every one of terra-draw's own internal helper features that SAME
// id — colliding with our already-stored feature (and each other) the moment
// SelectMode created its first midpoint/handle after `selectFeature`: a
// silent-to-the-caller "Feature already exists" from the store, which then
// starved the MapLibre adapter of the feature it expected and crashed
// downstream with "Cannot read properties of undefined (reading 'setData')".
// `@wehunt/map`'s own unit tests never caught this: `VertexEditSession.test
// .tsx`'s fake `TerraDraw.addFeatures`/`selectFeature` just record the call,
// they never simulate the real library minting ids for its own handles — the
// same class of test-fake blindness the `groundHitWidthExpression` fix
// (mapGeoJson.ts) hit for the same underlying reason (real-engine-only
// behavior a fake doesn't reproduce).
//
// `getId` below mirrors the library's OWN default strategy exactly — a UUID4
// generator, read directly out of the installed terra-draw.modern.js rather
// than assumed — so every one of those internal features gets a genuinely
// unique id, the way it would if this file supplied no idStrategy at all.
// `isValidId` accepts either shape: our fixed constant (why a custom
// strategy exists at all — the default's own `isValidId` requires a
// 36-character string and rejects our constant outright) or a 36-character
// string (the default's own acceptance rule for its self-minted UUIDs, same
// source).
const idStrategy = {
  getId: (): string =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    }),
  isValidId: (id: unknown): id is string => id === FEATURE_ID || (typeof id === 'string' && id.length === 36),
}

// terra-draw's own adapter layer ids default to a "td-" prefix, which
// MapView's raster z-order anchoring (vectorAnchorLayerId in MapView.tsx)
// never sees — it only recognises ids starting with GEOJSON_LAYER_PREFIX
// ("gj-"). Prefixing with "gj-td" instead makes terra-draw's layers visible
// to that anchor search, so a later raster-overlay reconciliation still
// slots underneath them rather than painting over the edit handles.
const ADAPTER_LAYER_PREFIX = `${GEOJSON_LAYER_PREFIX}td`

// terra-draw's own default (9 decimal degrees, sub-millimeter at the
// equator) is ample for any real land-survey precision our GeoPt data
// carries — set explicitly per the plan's obligation rather than relying on
// a library default silently staying put across a version bump.
const COORDINATE_PRECISION = 9

// terra-draw does not merely ROUND an over-precise coordinate — it REJECTS the
// whole feature. Its polygon/line validators run every coordinate through a
// decimal-place counter (`for (; Math.round(v*e)/e !== v; ) e *= 10, i++`, read
// directly in the installed terra-draw.modern.js) and fail the feature with
// "Feature has coordinates with excessive precision" when that count exceeds
// `coordinatePrecision`. Float64 arithmetic produces such values constantly:
// `25.2 + 0.08` is `25.279999999999998`, which counts as 15 decimals, and every
// ring built by adding degrees (fixtures, imported grounds, an inserted
// midpoint the app computed itself) carries at least one.
//
// `addFeatures` reports that rejection only in its RETURN VALUE — it neither
// throws nor logs — so before this rounding existed the feature simply was not
// in the store, and the very next line (`selectFeature`) threw the misleading
// "No feature with this id (…), can not get properties copy". Every real shape
// hit it; the unit-test fake accepted everything, so nothing caught it until
// the session first mounted in a browser.
//
// Rounding here rather than raising the precision is deliberate: terra-draw
// rounds every coordinate IT emits to this same precision anyway, so feeding it
// anything finer would guarantee that `pointsRef`'s baseline and terra-draw's
// stored geometry disagree on the very first diff — reporting a spurious
// vertex move at whichever index the float noise happened to land on. 1e-9° is
// ~0.1mm.
function roundCoordinate(value: number): number {
  const factor = 10 ** COORDINATE_PRECISION
  return Math.round(value * factor) / factor
}

function roundPoints(points: GeoPoint[]): GeoPoint[] {
  return points.map((p) => ({ lat: roundCoordinate(p.lat), lon: roundCoordinate(p.lon) }))
}

// Everything `TerraDrawMapLibreGLAdapter.register()` puts on the map: THREE
// geojson sources (`<prefix>-polygon`, `-linestring`, `-point`) and FIVE
// layers — one per source, plus `<prefix>-polygon-outline` (the polygon fill's
// companion line layer) and `<prefix>-point-marker` (the point layer's
// companion symbol layer). Read out of the installed adapter's `register` /
// `_addGeoJSONLayer` / `_addLayer`.
//
// This component sweeps them itself. `stop()` is the primary teardown and the
// installed adapter's `unregister()` does remove all five layers and all three
// sources (checked in every build of the locked version), so on the normal path
// this sweep removes nothing. It earns its place for two reasons:
//
//  - **It is the ONLY unwind path when setup throws.** React never receives a
//    cleanup function from an effect that threw, so a failure part-way through
//    `start()`/`addFeatures()`/`selectFeature()` leaves everything already on
//    the map with nothing scheduled to take it off. The `catch` in the mount
//    effect below calls this for exactly that case.
//  - **It is a version guard.** The adapter's teardown is upstream code we do
//    not control, and an adapter that ever fails to unregister fully would fail
//    SILENTLY here: MapLibre refuses to remove a source any layer still
//    references by firing an `ErrorEvent` and returning rather than throwing
//    (maplibre-gl `src/style/style.ts`), so a leftover layer quietly keeps its
//    source alive.
//
// Both matter because a leftover SOURCE is not a cosmetic leak — the next
// mount's `addSource` throws `Source "gj-td-polygon" already exists.` and takes
// the whole editor to the error boundary. The failure mode of an incomplete
// teardown is a crash on the NEXT edit, with nothing visible at the time it
// happened.
const ADAPTER_LAYER_IDS = [
  `${ADAPTER_LAYER_PREFIX}-polygon-outline`,
  `${ADAPTER_LAYER_PREFIX}-polygon`,
  `${ADAPTER_LAYER_PREFIX}-linestring`,
  `${ADAPTER_LAYER_PREFIX}-point-marker`,
  `${ADAPTER_LAYER_PREFIX}-point`,
]
const ADAPTER_SOURCE_IDS = [
  `${ADAPTER_LAYER_PREFIX}-polygon`,
  `${ADAPTER_LAYER_PREFIX}-linestring`,
  `${ADAPTER_LAYER_PREFIX}-point`,
]

/** Layers first, then sources: MapLibre will not remove a source while any
 *  layer still references it. Idempotent — the normal path finds nothing. */
function removeAdapterResidue(map: NonNullable<ReturnType<typeof useMap>>): void {
  for (const id of ADAPTER_LAYER_IDS) if (map.getLayer(id)) map.removeLayer(id)
  for (const id of ADAPTER_SOURCE_IDS) if (map.getSource(id)) map.removeSource(id)
}

const DRAW_MODE_NAME: Record<'polygon' | 'line', string> = {
  polygon: 'polygon',
  line: 'linestring',
}

function pointsEqual(a: GeoPoint, b: GeoPoint): boolean {
  return a.lat === b.lat && a.lon === b.lon
}

function pointArraysEqual(a: GeoPoint[], b: GeoPoint[]): boolean {
  return a.length === b.length && a.every((p, i) => pointsEqual(p, b[i]))
}

function toPositions(points: GeoPoint[]): Position[] {
  return points.map((p) => [p.lon, p.lat])
}

function fromPositions(positions: Position[]): GeoPoint[] {
  return positions.map(([lon, lat]) => ({ lat, lon }))
}

/**
 * Validates the "single-ring polygon or open line" precondition the props
 * doc comment states, and throws rather than silently handing terra-draw (or
 * this component's own diffing) a shape it wasn't built for.
 *
 * `shape.points` is a flat point list — it cannot structurally carry more
 * than one ring, so this can never catch a true multi-ring/holed GeoArea
 * reaching this component (that precondition is `geometryEditable`'s job,
 * upstream, per the plan). What it CAN catch, and does: a caller passing an
 * ALREADY-CLOSED ring (points[0] duplicated at the end). Our own closing
 * step in `toGeometry` always appends points[0] again — feeding it an
 * already-closed ring would double the seam, and every index this component
 * reports afterwards would be one off from the caller's own (unclosed) point
 * list from then on. That silent index drift is exactly the "corrupts
 * instead of throwing" failure mode this assertion exists to prevent.
 */
function assertValidShape(shape: VertexEditSessionProps['shape']): void {
  const { kind, points } = shape
  if (kind === 'polygon') {
    if (points.length < 3) {
      throw new Error(`VertexEditSession: a polygon needs at least 3 points, got ${points.length}`)
    }
    if (pointsEqual(points[0], points[points.length - 1])) {
      throw new Error(
        'VertexEditSession: polygon points must be an open (unclosed) single ring, but the first and ' +
          'last points are identical — this indicates an already-closed ring or a multi-ring shape, ' +
          'neither of which this component can safely edit',
      )
    }
  } else if (points.length < 2) {
    throw new Error(`VertexEditSession: a line needs at least 2 points, got ${points.length}`)
  }
}

/** Our `points` are unclosed; a terra-draw Polygon ring is closed (first
 *  coordinate repeated at the end, per the GeoJSON spec terra-draw enforces).
 *  This is the one place that convention is applied — every other function
 *  in this file works in the open representation. */
function toGeometry(kind: 'polygon' | 'line', points: GeoPoint[]): Polygon | LineString {
  const positions = toPositions(points)
  if (kind === 'polygon') {
    return { type: 'Polygon', coordinates: [[...positions, positions[0]]] }
  }
  return { type: 'LineString', coordinates: positions }
}

/** Inverse of `toGeometry`: strips the polygon ring's closing duplicate.
 *  Asserts the ring is single (`coordinates.length === 1`) — terra-draw
 *  itself refuses to hold a holed/multi-ring Polygon at all (verified in the
 *  spike: `addFeatures` rejects it with `"Feature has holes"` unconditionally,
 *  even past a custom validator), so more than one ring coming back here can
 *  only mean a wiring bug in this file, never real user data. */
function fromGeometry(kind: 'polygon' | 'line', geometry: Polygon | LineString): GeoPoint[] {
  if (kind === 'polygon') {
    const geom = geometry as Polygon
    if (geom.coordinates.length !== 1) {
      throw new Error(
        `VertexEditSession: expected a single-ring Polygon back from terra-draw, got ${geom.coordinates.length} rings`,
      )
    }
    return fromPositions(geom.coordinates[0].slice(0, -1))
  }
  return fromPositions((geometry as LineString).coordinates)
}

type PointDiff =
  | { kind: 'move'; index: number; point: GeoPoint }
  | { kind: 'insert'; afterIndex: number; point: GeoPoint }
  | { kind: 'delete'; index: number }
  | { kind: 'none' }

/**
 * terra-draw reports whole-feature changes (a `change` event names the
 * feature id, not which vertex moved) — this derives which vertex moved,
 * was inserted, or was deleted by diffing the new open-point array against
 * the previously-known one:
 *
 *   - equal length: exactly one vertex differs → that index MOVED.
 *   - +1 length: the arrays share a common prefix up to some index `i`
 *     (or share the WHOLE shorter array as a prefix, if the insertion is at
 *     the very end) — `next[i]` is the new vertex, inserted after `i - 1`.
 *   - -1 length: same prefix logic; the vertex at that index was DELETED.
 *
 * The "prefix" framing also covers a polygon's wrap-around segment (between
 * the last vertex and the first) for free: inserting a midpoint there lands
 * in terra-draw's closed ring just before the closing duplicate, which
 * `fromGeometry` strips — so in the OPEN representation this component
 * works in, that insertion appears as a plain append at the end of the
 * array, i.e. `afterIndex === previous.length - 1`. No special-casing needed.
 * Index 0 (and, symmetrically, deleting/moving it) needs no special-casing
 * either — it is just `divergedAt === 0` like any other index.
 *
 * Controller-ruled edge case: when two ADJACENT points are equal, an insert
 * or delete touching one of that pair is genuinely ambiguous by value alone
 * — e.g. previous [p0,p1,p2] vs. next [p0,p1,p1,p2] could mean "a duplicate
 * of p1 was inserted after p1" (afterIndex 1) or "a duplicate of p1 was
 * inserted BEFORE the existing p1" (afterIndex 0, with the original p1
 * shifting to index 2) — the resulting array is byte-identical either way.
 * This scan always attributes such an event to the LATER index (the first
 * position, scanning from the front, where the two arrays diverge) because
 * the coordinates reported are identical regardless of which reading is
 * "true" — there is no observable difference for the caller's history model
 * to get right or wrong, only an index label, and the later-index reading is
 * the simplest one to keep deterministic. See the "duplicate adjacent
 * points" tests for the pinned behavior.
 */
function diffPoints(previous: GeoPoint[], next: GeoPoint[]): PointDiff {
  const shorterLength = Math.min(previous.length, next.length)
  let divergedAt = shorterLength
  for (let i = 0; i < shorterLength; i++) {
    if (!pointsEqual(previous[i], next[i])) {
      divergedAt = i
      break
    }
  }

  if (next.length === previous.length) {
    return divergedAt === shorterLength ? { kind: 'none' } : { kind: 'move', index: divergedAt, point: next[divergedAt] }
  }
  if (next.length === previous.length + 1) {
    return { kind: 'insert', afterIndex: divergedAt - 1, point: next[divergedAt] }
  }
  if (next.length === previous.length - 1) {
    return { kind: 'delete', index: divergedAt }
  }
  // Our select-mode flags (below) enable exactly one vertex operation per
  // gesture (drag, midpoint-insert, or delete) — a length delta outside
  // {-1, 0, +1} cannot come from real terra-draw behavior under that config,
  // only from a wiring bug (e.g. this component's own echo guard failing to
  // filter a resync). Throwing surfaces that immediately instead of quietly
  // reporting a wrong index to the caller's history model.
  throw new Error(
    `VertexEditSession: unexpected point-count change (${previous.length} -> ${next.length}) in one terra-draw ` +
      'change event — only single vertex move/insert/delete are wired to this component',
  )
}

// terra-draw types its styling knobs as a `#`-prefixed hex literal, stricter
// than this component's own `style.stroke`/`style.fill` (any CSS color
// string, per the props doc comment). terra-draw does not itself validate
// hex format at runtime — it hands the value straight through to maplibre's
// paint properties, which accept any valid CSS color — so this cast is safe
// in practice even though the literal type is narrower than what we accept.
function asHexColor(value: string): `#${string}` {
  return value as `#${string}`
}

function polygonBaseStyles(style: VertexEditSessionProps['style']) {
  return {
    outlineColor: asHexColor(style.stroke),
    ...fillStyles(style.fill, 'fillColor', 'fillOpacity'),
  }
}

// terra-draw has no "no fill" colour: an absent colour falls back to its own
// default at 0.3 opacity, so an unfilled polygon is spelled as opacity 0.
function fillStyles<C extends string, O extends string>(
  fill: string | null | undefined,
  colorKey: C,
  opacityKey: O,
): Partial<Record<C, `#${string}`> & Record<O, number>> {
  if (fill === undefined) return {}
  if (fill === null) return { [opacityKey]: 0 } as Partial<Record<C, `#${string}`> & Record<O, number>>
  return { [colorKey]: asHexColor(fill) } as Partial<Record<C, `#${string}`> & Record<O, number>>
}

function lineBaseStyles(style: VertexEditSessionProps['style']) {
  return { lineStringColor: asHexColor(style.stroke) }
}

// selectedPolygonColor -> polygonFillColor and selectedPolygonOutlineColor ->
// polygonOutlineColor, confirmed by reading TerraDrawSelectMode.styleFeature
// in the installed terra-draw.modern.js (see the task report for the exact
// source excerpt) — "Color" alone means fill, matching the base
// TerraDrawAdapterStyling naming (polygonFillColor vs polygonOutlineColor).
function selectStyles(style: VertexEditSessionProps['style']) {
  return {
    selectedPolygonOutlineColor: asHexColor(style.stroke),
    selectedLineStringColor: asHexColor(style.stroke),
    ...fillStyles(style.fill, 'selectedPolygonColor', 'selectedPolygonFillOpacity'),
  }
}

interface Session {
  td: TerraDraw
  kind: 'polygon' | 'line'
}

/** Lifts one shape (polygon or open line) into a terra-draw select-mode
 *  session so it can be dragged/midpoint-inserted/deleted by the user, and
 *  reports every change back as an index-addressed callback. Renders nothing
 *  itself — see the module doc comment on each piece for the mechanics:
 *  the echo-guard (context.origin === 'api'), the closing-coordinate
 *  convention (`toGeometry`/`fromGeometry`), and the diffing rules
 *  (`diffPoints`). */
export function VertexEditSession(props: VertexEditSessionProps): null {
  // Validated before any hook runs (see assertValidShape's own doc comment):
  // a shape that fails this can never leave hooks in an inconsistent state
  // across renders, because a failing render never reaches a hook call.
  assertValidShape(props.shape)

  const { shape, style, onVertexMove, onVertexInsert, onVertexDelete, onDragStart } = props
  const map = useMap()

  // Latest props at mount-effect run time, same pattern as MapMarker's
  // anchor/rotation/draggable: updated on every render, but only READ once,
  // inside the create-effect below (keyed on `[map]`), at whatever render
  // that effect actually fires on — not necessarily the very first one. The
  // mode wiring below (which draw-mode name backs the feature) is fixed for
  // the whole life of the terra-draw instance once created. A `shape.kind`
  // change after that point is not a supported transition — remount the
  // component (a fresh `key`) to edit a different kind of shape.
  const initialRef = useRef({ kind: shape.kind, points: shape.points, style })
  initialRef.current = { kind: shape.kind, points: shape.points, style }

  const onVertexMoveRef = useRef(onVertexMove)
  onVertexMoveRef.current = onVertexMove
  const onVertexInsertRef = useRef(onVertexInsert)
  onVertexInsertRef.current = onVertexInsert
  const onVertexDeleteRef = useRef(onVertexDelete)
  onVertexDeleteRef.current = onVertexDelete
  const onDragStartRef = useRef(onDragStart)
  onDragStartRef.current = onDragStart

  const sessionRef = useRef<Session | null>(null)
  // The last point array terra-draw's feature is known to hold — both the
  // baseline the diffing above compares an incoming `change` event against,
  // and the baseline the prop-resync effect compares `shape.points` against
  // to skip a no-op `updateFeatureGeometry` call. Updated by BOTH the change
  // handler (after emitting a derived event) and the resync effect (after
  // pushing a prop-driven update) — it always reflects terra-draw's actual
  // current state, never a stale prop snapshot.
  const pointsRef = useRef<GeoPoint[]>(shape.points)
  // Whether a user gesture is currently in progress. terra-draw has no
  // top-level "drag started" EVENT (TerraDrawEventListeners only carries
  // ready/finish/change/select/deselect/history) — TerraDrawSelectMode's own
  // onDragStart is a private, internal method, never surfaced. The first
  // real (non-echo) `change` event since the last `finish` is therefore the
  // most honest signal this component's integration surface has for "a
  // gesture just started": `finish` reliably closes out every select-mode
  // gesture this session enables (drag, insert, delete — all appear in
  // OnFinishContext's `action` union per the spike), so it's a real
  // boundary, not a guess.
  const gestureActiveRef = useRef(false)
  const styleRef = useRef(style)
  // Set by the resync effect below when it defers a prop-driven
  // `updateFeatureGeometry` because a gesture is in progress (see IMPORTANT
  // 5 in the task report: a resync must never yank geometry out from under
  // an in-progress drag/insert/delete). Applied by `handleFinish`, inside
  // the create-effect, once that gesture ends. `null` means "no resync is
  // owed."
  const pendingResyncRef = useRef<GeoPoint[] | null>(null)

  useEffect(() => {
    if (!map) return
    const initial = initialRef.current
    const kind = initial.kind
    const modeName = DRAW_MODE_NAME[kind]

    const drawMode =
      kind === 'polygon'
        ? new TerraDrawPolygonMode({ styles: polygonBaseStyles(initial.style) })
        : new TerraDrawLineStringMode({ styles: lineBaseStyles(initial.style) })

    const selectMode = new TerraDrawSelectMode({
      flags: {
        [modeName]: {
          feature: {
            // No whole-feature drag surface on this component (no callback
            // for it) — only vertex-level edits are exposed.
            draggable: false,
            // A stored shape that ALREADY crosses itself must stay editable.
            // Left at its default (false), terra-draw vetoes any coordinate
            // drag whose resulting geometry self-intersects — and it tests the
            // WHOLE ring, not just the two segments the dragged vertex moved.
            // So a ring that arrives crossed rejects every drag of every
            // vertex before the store is touched: no geometry update, no
            // 'change' event, nothing for this component to report. The user
            // sees the handles appear, grabs one, and the shape does not budge
            // — including the drag that would have UNCROSSED it. Hand-drawn
            // hunting grounds do cross themselves in production, and the
            // platform treats that as data to repair (the .NET area
            // calculation runs Buffer(0) over such a ring) rather than data to
            // refuse — so vetoing the reshape here would be the editor alone
            // holding a line nothing else holds.
            selfIntersectable: true,
            coordinates: {
              draggable: true,
              midpoints: true,
              deletable: true,
            },
          },
        },
      },
      // The lifted feature must stay selected (handles visible, editable)
      // for this component's entire mounted lifetime — deselecting is a
      // "commit"/"cancel" decision the HUD owns, not something an incidental
      // Escape keypress or empty-space click should be able to do underneath
      // it. terra-draw defaults `allowManualDeselection` to true.
      //
      // IMPORTANT: `allowManualDeselection` alone does NOT block Escape.
      // Read directly in the installed terra-draw.modern.js:
      // TerraDrawSelectMode.onKeyUp's deselect branch calls `this.cleanUp()`
      // (-> deselect) unconditionally whenever `key === keyEvents.deselect`
      // — allowManualDeselection is checked only in the click-based select()
      // path, never here. Worse, terra-draw's default `keyEvents.delete`
      // ('Delete') is bound to `deleteSelected()`, which deletes the WHOLE
      // FEATURE at the store level (ignoring `coordinates.deletable`
      // entirely) and fires a 'delete'-type change event — exactly the
      // event type this component's `handleChange` discards, so a user
      // pressing Delete would silently vanish the shape from terra-draw
      // while this component never found out. `keyEvents: null` disarms
      // every one of terra-draw's default keyboard gestures (delete,
      // deselect, rotate, scale) in one call — verified against the
      // library's own `updateOptions`: passing `null` here (as opposed to
      // an object) takes the dedicated "all off" branch, setting every key
      // to `null` at once. This component never enabled rotate/scale via
      // ModeFlags anyway, so nothing here removes a capability we wanted;
      // it only removes surfaces we never asked for. The real per-vertex
      // delete gesture is UNAFFECTED by any of this: it's `onRightClick`,
      // gated on `coordinates.deletable` (set above), a completely separate
      // code path from `keyEvents.delete` — see the task report for the
      // read of `onRightClick`'s source and the corrected gesture-parity
      // note for Task 4.
      allowManualDeselection: false,
      keyEvents: null,
      styles: selectStyles(initial.style),
    })

    const adapter = new TerraDrawMapLibreGLAdapter({
      map,
      prefixId: ADAPTER_LAYER_PREFIX,
      coordinatePrecision: COORDINATE_PRECISION,
    })

    const td = new TerraDraw({
      adapter,
      modes: [drawMode, selectMode],
      idStrategy,
    })

    const points = roundPoints(initial.points)
    try {
      // Residue from a teardown that could not complete — see
      // `ADAPTER_LAYER_IDS`. A normal mount finds nothing to remove.
      removeAdapterResidue(map)
      td.start()
      const added = td.addFeatures([
        {
          id: FEATURE_ID,
          type: 'Feature',
          properties: { mode: modeName },
          geometry: toGeometry(kind, points),
        } as GeoJSONStoreFeatures,
      ])
      // `addFeatures` reports a rejected feature ONLY here: it does not throw
      // and does not log. Left unchecked, the rejection surfaced two lines
      // down as `selectFeature`'s "No feature with this id (…), can not get
      // properties copy" — an error that says nothing about the actual cause.
      // Every reason terra-draw can give is a wiring bug at this point (holes
      // are `resolveVertexEditTarget`'s job, point counts and ring closure are
      // `assertValidShape`/`toGeometry`'s, precision is `roundPoints`'s), so
      // fail loudly and name the reason.
      const rejected = added.find((r) => !r.valid)
      if (rejected) {
        throw new Error(
          `VertexEditSession: terra-draw rejected the lifted feature — ${rejected.reason ?? 'no reason given'}`,
        )
      }
      td.setMode('select')
      td.selectFeature(FEATURE_ID)
    } catch (setupError) {
      // An effect that THROWS never returns its cleanup function, so React has
      // nothing to call and everything `start()` already put on the map stays
      // there — and the NEXT mount then dies on `addSource` with `Source
      // "gj-td-polygon" already exists.`, which is the error a user sees
      // instead of the one that actually went wrong. Unwind by hand, then
      // rethrow the real cause.
      //
      // `stop()` can itself throw on a half-registered adapter (its `clear()`
      // calls `setData` on sources a `register()` that failed part-way never
      // added), so it is attempted separately. The sweep after it is what
      // actually guarantees a clean map; `setupError` propagates either way.
      try {
        td.stop()
      } catch {
        // Deliberately not reported: nothing this secondary failure can say is
        // more useful than `setupError`, and rethrowing it would replace the
        // real cause with a consequence of it.
      }
      removeAdapterResidue(map)
      throw setupError
    }

    pointsRef.current = points
    styleRef.current = initial.style
    gestureActiveRef.current = false
    pendingResyncRef.current = null

    const handleChange: TerraDrawEventListeners['change'] = (ids, type, context) => {
      if (!ids.includes(FEATURE_ID)) return
      // Echo guard: terra-draw tags every programmatic store mutation with
      // `context.origin === 'api'` (verified against the installed library —
      // see the task report). Our own `updateFeatureGeometry` call in the
      // resync effect below is exactly such a mutation, so filtering on this
      // flag is what stops that resync from looping back through this
      // handler and re-emitting a callback for a change WE caused.
      if (context && 'origin' in context && context.origin === 'api') return
      if (type !== 'update') return
      // Second, independent filter (defense in depth, not redundant with the
      // echo guard above): a REAL, non-'api' 'update' change is not always a
      // geometry edit. Read directly in the installed terra-draw.modern.js:
      // every store mutation is tagged with a `target` — GeoJSONStore's
      // `updateGeometry` always merges in `{target:"geometry"}`,
      // `updateProperty` always merges in `{target:"properties"}` — so a
      // selection-state change (e.g. Escape -> deselect, which calls
      // `mutateFeature.setDeselected`, a PROPERTIES update) arrives here as
      // `type: 'update'` too, with no `origin` key (it's a real gesture, not
      // an API call) but `target: 'properties'`. Without this check that
      // event would be misread as a geometry edit: it would wrongly fire
      // onDragStart (there's no drag), and the geometry-derived diff below
      // would run against UNCHANGED coordinates (`diffPoints` returns
      // `{kind:'none'}`, so no vertex callback fires here) — but the
      // spurious onDragStart would still corrupt the NEXT real gesture's own
      // onDragStart signal by leaving gestureActiveRef wrongly `true` until
      // the next `finish`. Requiring `target === 'geometry'` closes that
      // hole regardless of which keyboard/API path produces the
      // properties-only change.
      if (context?.target !== 'geometry') return

      if (!gestureActiveRef.current) {
        gestureActiveRef.current = true
        onDragStartRef.current?.()
      }

      const feature = td.getSnapshotFeature(FEATURE_ID)
      if (!feature) return
      const newPoints = fromGeometry(kind, feature.geometry as Polygon | LineString)
      const diff = diffPoints(pointsRef.current, newPoints)
      pointsRef.current = newPoints
      switch (diff.kind) {
        case 'move':
          onVertexMoveRef.current(diff.index, diff.point.lat, diff.point.lon)
          break
        case 'insert':
          onVertexInsertRef.current(diff.afterIndex, diff.point.lat, diff.point.lon)
          break
        case 'delete':
          onVertexDeleteRef.current(diff.index)
          break
        case 'none':
          break
      }
    }

    const handleFinish: TerraDrawEventListeners['finish'] = (id) => {
      if (id !== FEATURE_ID) return
      gestureActiveRef.current = false
      // Apply a resync the effect below deferred while this gesture was in
      // progress (IMPORTANT 5 in the task report): a prop-driven
      // `updateFeatureGeometry` must never yank geometry out from under an
      // in-progress drag/insert/delete, so the resync effect stashes the
      // latest desired points here instead of calling terra-draw directly
      // while `gestureActiveRef.current` is true. Now that the gesture has
      // closed out, push it — this call is tagged `origin: 'api'` by
      // terra-draw like any other, so the echo guard above still prevents it
      // from re-triggering a vertex callback.
      const pending = pendingResyncRef.current
      pendingResyncRef.current = null
      if (pending && !pointArraysEqual(pointsRef.current, pending)) {
        td.updateFeatureGeometry(FEATURE_ID, toGeometry(kind, pending))
        pointsRef.current = pending
      }
    }

    td.on('change', handleChange)
    td.on('finish', handleFinish)
    sessionRef.current = { td, kind }

    return () => {
      td.off('change', handleChange)
      td.off('finish', handleFinish)
      // `stop()` clears the store, deregisters the adapter, and removes any
      // rendered layers in the process (per terra-draw's own doc comment on
      // the method) — the one call that covers every piece of this task's
      // "features removed, TerraDraw stopped/destroyed, adapter detached"
      // cleanup requirement. `removeAdapterResidue` after it is the sweep
      // described on `ADAPTER_LAYER_IDS`; on the normal path it removes
      // nothing.
      //
      // Both are skipped when the map no longer holds ANY of the adapter's
      // sources, which is exactly the state after the whole editor unmounts:
      // React destroys a deleted subtree parent-first, so `MapView`'s own
      // cleanup (`map.remove()`) has already run by the time this one does.
      // `stop()` would then have the adapter's `clear()` call `setData` on a
      // source that is gone — a TypeError thrown out of a cleanup function,
      // which React reports straight to the error boundary, turning "the user
      // navigated away while editing points" into an error screen. There is
      // nothing to unwind in that case: the map and every layer on it are
      // already destroyed.
      if (ADAPTER_SOURCE_IDS.some((id) => map.getSource(id))) {
        td.stop()
        removeAdapterResidue(map)
      }
      sessionRef.current = null
    }
    // Mount/unmount only: re-creating the terra-draw instance on every kind
    // or style change would tear down and rebuild the user's in-progress
    // edit session (losing handle focus, drag state, ...). Kind is captured
    // once via initialRef (see its doc comment); style changes are applied
    // live below instead of by recreating the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  // Controlled-component resync: `shape.points` is the source of truth. A
  // prop change that doesn't match what terra-draw is already showing pushes
  // an `updateFeatureGeometry` call — which the echo guard above then
  // prevents from re-triggering a callback. Value-compared against
  // `pointsRef` (not just a reference-equality dependency) so a parent that
  // maps a fresh array each render with the SAME values doesn't churn
  // terra-draw on every unrelated re-render.
  //
  // Deferred while a gesture is in progress (`gestureActiveRef.current`):
  // calling `updateFeatureGeometry` mid-drag would silently overwrite
  // whatever the user is actively dragging with a stale prop value — the
  // history model hasn't even seen the in-progress move yet, since
  // `onVertexMove` fires from `handleChange`, not synchronously with the
  // prop. Instead the desired points are stashed in `pendingResyncRef` and
  // applied by `handleFinish` (inside the create-effect) once the gesture
  // closes out. The invariant: a resync never yanks geometry mid-gesture,
  // it only ever lands between gestures.
  //
  // Rounded to `COORDINATE_PRECISION` first, for the same two reasons the
  // mount path rounds (see `roundCoordinate`): terra-draw would reject an
  // over-precise geometry outright, and `pointsRef` must hold exactly what
  // terra-draw holds or the next gesture's diff reports the wrong vertex.
  useEffect(() => {
    const session = sessionRef.current
    if (!session) return
    const desired = roundPoints(shape.points)
    if (pointArraysEqual(pointsRef.current, desired)) {
      pendingResyncRef.current = null
      return
    }
    if (gestureActiveRef.current) {
      pendingResyncRef.current = desired
      return
    }
    session.td.updateFeatureGeometry(FEATURE_ID, toGeometry(session.kind, desired))
    pointsRef.current = desired
    pendingResyncRef.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape.points])

  // Live re-styling: unlike `shape.kind`, a style change (e.g. a different
  // accent color when a boundary's own selection color changes) is a
  // supported live transition — applied via terra-draw's own dynamic
  // options update rather than recreating the session.
  useEffect(() => {
    const session = sessionRef.current
    if (!session) return
    if (styleRef.current.stroke === style.stroke && styleRef.current.fill === style.fill) return
    // `updateModeOptions` merges — per its own doc comment ("this allows
    // _partial_ updating of the modes options"), passing only `styles` here
    // leaves `flags`/`allowManualDeselection`/`keyEvents` set above
    // untouched; it does not reset them to their own defaults. Confirmed
    // against `TerraDrawSelectMode.updateOptions` in the installed
    // terra-draw.modern.js, which itself does a keyed `Object.assign` per
    // option (cursors/keyEvents/dragEventThrottle/allowManualDeselection/
    // allowManualSelection/flags), never a wholesale replace — so this call
    // is safe to make with a `styles`-only payload on every style change.
    session.td.updateModeOptions('select', { styles: selectStyles(style) })
    styleRef.current = style
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style.stroke, style.fill])

  return null
}
